import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useApi } from "@/lib/api";
import { useBrand } from "@/lib/brand";
import { PageHeader, SectionTitle, PageLoader, StatusBadge, EmptyState, Btn, TextInput, TextArea, BRAND_ACCENTS } from "@/lib/ui";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Share2, Facebook, Instagram, Youtube, Linkedin, Sparkles, Send, CalendarClock,
  Check, X, Trash2, PencilLine, CheckCircle2, Circle, Wand2, Globe,
} from "lucide-react";
import { cn } from "@/lib/utils";

const hsl = (v) => `hsl(${v || "46 68% 52%"})`;
const PLAT = {
  facebook: { label: "Facebook", icon: Facebook },
  instagram: { label: "Instagram", icon: Instagram },
  youtube: { label: "YouTube", icon: Youtube },
  linkedin: { label: "LinkedIn", icon: Linkedin },
};
const PLATFORMS = ["facebook", "instagram", "youtube", "linkedin"];

/* ---------------- Connections tab ---------------- */
function ConnectDialog({ open, onClose, brand, platform }) {
  const api = useApi();
  const qc = useQueryClient();
  const [form, setForm] = useState({ handle: "", account_id: "", page_id: "", access_token: "" });
  React.useEffect(() => {
    if (open && brand && platform) {
      const c = brand.platforms?.[platform]?.config || {};
      setForm({ handle: c.handle || "", account_id: c.account_id || "", page_id: c.page_id || "", access_token: "" });
    }
  }, [open, brand?.code, platform]); // eslint-disable-line
  const inval = () => { qc.invalidateQueries({ queryKey: ["social-connections"] }); qc.invalidateQueries({ queryKey: ["social-overview"] }); };
  const save = useMutation({
    mutationFn: () => api.put(`/social/connections/${brand.code}/${platform}`, form),
    onSuccess: () => { toast.success(`${PLAT[platform].label} saved for ${brand.name}`); inval(); onClose(); },
    onError: (e) => toast.error(e.message),
  });
  const disconnect = useMutation({
    mutationFn: () => api.del(`/social/connections/${brand.code}/${platform}`),
    onSuccess: () => { toast.success("Disconnected"); inval(); onClose(); },
    onError: (e) => toast.error(e.message),
  });
  if (!brand || !platform) return null;
  const connected = brand.platforms?.[platform]?.status === "connected";
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-surface border-border max-w-md">
        <DialogHeader>
          <DialogTitle>{PLAT[platform].label} · {brand.name}</DialogTitle>
          <DialogDescription>Add the account handle now. Paste the API access token when you complete app review to enable auto-posting.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <TextInput label="Account handle / page name" value={form.handle} onChange={(e) => setForm((s) => ({ ...s, handle: e.target.value }))} data-testid="social-field-handle" />
          <TextInput label="Account / Page ID (optional)" value={form.account_id} onChange={(e) => setForm((s) => ({ ...s, account_id: e.target.value }))} data-testid="social-field-account_id" />
          <TextInput label="Access token (optional, stored securely)" value={form.access_token} onChange={(e) => setForm((s) => ({ ...s, access_token: e.target.value }))} data-testid="social-field-token" />
          <div className="flex items-center gap-2 pt-1">
            <Btn onClick={() => save.mutate()} disabled={save.isPending} className="flex-1" data-testid="social-conn-save">{save.isPending ? "Saving…" : connected ? "Update" : "Connect"}</Btn>
            {connected && <Btn variant="outline" onClick={() => disconnect.mutate()} disabled={disconnect.isPending} data-testid="social-conn-disconnect">Disconnect</Btn>}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ConnectionsTab() {
  const api = useApi();
  const [dialog, setDialog] = useState({ open: false, brand: null, platform: null });
  const { data: rows = [], isLoading } = useQuery({ queryKey: ["social-connections"], queryFn: () => api.get("/social/connections") });
  if (isLoading) return <PageLoader />;
  return (
    <div data-testid="social-connections">
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
        {rows.map((b) => (
          <div key={b.code} className="neu-card rounded-2xl p-5" data-testid={`social-portal-${b.code}`} style={{ borderTop: `2px solid ${hsl(BRAND_ACCENTS[b.code] || b.accent)}` }}>
            <div className="flex items-center gap-2 mb-4">
              <span className="h-2.5 w-2.5 rounded-sm shrink-0" style={{ background: hsl(BRAND_ACCENTS[b.code] || b.accent) }} />
              <span className="text-sm font-medium truncate">{b.name}</span>
            </div>
            <div className="grid grid-cols-2 gap-2">
              {PLATFORMS.map((p) => {
                const cell = b.platforms[p]; const on = cell.status === "connected"; const Icon = PLAT[p].icon;
                return (
                  <button key={p} onClick={() => setDialog({ open: true, brand: b, platform: p })} data-testid={`social-connect-${b.code}-${p}`}
                    className="flex items-center gap-2 rounded-lg neu-inset px-3 py-2 text-left">
                    <Icon className="h-4 w-4 shrink-0" style={{ color: on ? "hsl(145 60% 62%)" : "hsl(0 0% 50%)" }} />
                    <div className="min-w-0 flex-1">
                      <div className="text-xs font-medium truncate">{PLAT[p].label}</div>
                      <div className="text-[10px] text-muted-foreground truncate">{on ? (cell.config.handle || "Connected") : "Not connected"}</div>
                    </div>
                    {on ? <CheckCircle2 className="h-3.5 w-3.5 text-ok shrink-0" /> : <Circle className="h-3.5 w-3.5 text-muted-foreground/40 shrink-0" />}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>
      <ConnectDialog open={dialog.open} brand={dialog.brand} platform={dialog.platform} onClose={() => setDialog({ open: false, brand: null, platform: null })} />
    </div>
  );
}

/* ---------------- Content pipeline tab ---------------- */
function GenerateDialog({ open, onClose, brand }) {
  const api = useApi();
  const qc = useQueryClient();
  const [topic, setTopic] = useState("");
  const [platforms, setPlatforms] = useState(["instagram"]);
  const [count, setCount] = useState(3);
  const toggle = (p) => setPlatforms((s) => s.includes(p) ? s.filter((x) => x !== p) : [...s, p]);
  const gen = useMutation({
    mutationFn: () => api.post("/social/generate", { brand_id: brand, topic, platforms, count }),
    onSuccess: (r) => { toast.success(`${r.created} draft(s) generated`); qc.invalidateQueries({ queryKey: ["social-posts"] }); qc.invalidateQueries({ queryKey: ["social-overview"] }); onClose(); },
    onError: (e) => toast.error(e.message),
  });
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-surface border-border max-w-md">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Wand2 className="h-4 w-4 text-primary" /> Generate content</DialogTitle>
          <DialogDescription>The AI drafts on-brand posts for {brand} using its positioning and recent activity. You approve before anything is scheduled.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <TextInput label="Topic (optional)" placeholder="e.g. festive collection launch" value={topic} onChange={(e) => setTopic(e.target.value)} data-testid="gen-topic" />
          <div>
            <span className="text-xs text-muted-foreground mb-1.5 block">Platforms</span>
            <div className="flex flex-wrap gap-2">
              {PLATFORMS.map((p) => (
                <button key={p} onClick={() => toggle(p)} data-testid={`gen-plat-${p}`}
                  className={cn("text-xs rounded-full px-3 py-1.5 border transition-colors", platforms.includes(p) ? "border-primary text-primary bg-primary/10" : "border-border text-muted-foreground")}>
                  {PLAT[p].label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <span className="text-xs text-muted-foreground mb-1.5 block">Number of drafts: {count}</span>
            <input type="range" min="1" max="6" value={count} onChange={(e) => setCount(+e.target.value)} className="w-full accent-primary" data-testid="gen-count" />
          </div>
        </div>
        <DialogFooter><Btn onClick={() => gen.mutate()} disabled={gen.isPending || platforms.length === 0} data-testid="gen-run"><Sparkles className="h-4 w-4" /> {gen.isPending ? "Generating…" : "Generate"}</Btn></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ScheduleDialog({ post, onClose }) {
  const api = useApi();
  const qc = useQueryClient();
  const [dt, setDt] = useState("");
  const sched = useMutation({
    mutationFn: () => api.post(`/social/posts/${post.id}/schedule`, { scheduled_at: new Date(dt).toISOString() }),
    onSuccess: () => { toast.success("Scheduled"); qc.invalidateQueries({ queryKey: ["social-posts"] }); qc.invalidateQueries({ queryKey: ["social-overview"] }); onClose(); },
    onError: (e) => toast.error(e.message),
  });
  return (
    <Dialog open={!!post} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-surface border-border max-w-sm">
        <DialogHeader><DialogTitle>Schedule post</DialogTitle><DialogDescription>Pick when this should go out. It publishes automatically once the account is connected.</DialogDescription></DialogHeader>
        <input type="datetime-local" value={dt} onChange={(e) => setDt(e.target.value)} data-testid="schedule-datetime"
          className="w-full h-9 rounded-md border border-border bg-surface px-3 text-sm" />
        <DialogFooter><Btn onClick={() => sched.mutate()} disabled={!dt || sched.isPending} data-testid="schedule-confirm"><CalendarClock className="h-4 w-4" /> Schedule</Btn></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const STATUS_TONE = { pending_approval: "warn", approved: "info", scheduled: "gold", published: "ok", rejected: "err", draft: "muted" };

function PostCard({ post, onSchedule }) {
  const api = useApi();
  const qc = useQueryClient();
  const inval = () => { qc.invalidateQueries({ queryKey: ["social-posts"] }); qc.invalidateQueries({ queryKey: ["social-overview"] }); };
  const act = useMutation({ mutationFn: ({ verb }) => api.post(`/social/posts/${post.id}/${verb}`), onSuccess: () => inval(), onError: (e) => toast.error(e.message) });
  const del = useMutation({ mutationFn: () => api.del(`/social/posts/${post.id}`), onSuccess: () => { toast.success("Deleted"); inval(); }, onError: (e) => toast.error(e.message) });
  return (
    <div className="neu-inset rounded-xl p-3.5 space-y-2" data-testid={`social-post-${post.id}`}>
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-1">
          {(post.platforms || []).map((p) => { const Icon = PLAT[p]?.icon; return Icon ? <Icon key={p} className="h-3.5 w-3.5 text-muted-foreground" /> : null; })}
          {post.ai_generated && <Sparkles className="h-3 w-3 text-primary" title="AI generated" />}
        </div>
        <StatusBadge tone={STATUS_TONE[post.status]} dot>{post.status.replace("_", " ")}</StatusBadge>
      </div>
      {post.hook && <div className="text-sm font-medium">{post.hook}</div>}
      <p className="text-xs text-foreground/70 whitespace-pre-wrap">{post.caption}</p>
      {(post.hashtags || []).length > 0 && <div className="text-[11px] text-primary/80 font-mono">{post.hashtags.map((h) => `#${h}`).join(" ")}</div>}
      {post.scheduled_at && <div className="text-[11px] text-muted-foreground flex items-center gap-1"><CalendarClock className="h-3 w-3" /> {new Date(post.scheduled_at).toLocaleString()}</div>}
      <div className="flex items-center gap-1 pt-1 border-t border-border/50 flex-wrap">
        {post.status === "pending_approval" && (
          <>
            <Btn size="sm" variant="ghost" onClick={() => act.mutate({ verb: "approve" })} data-testid={`post-approve-${post.id}`}><Check className="h-3.5 w-3.5 text-ok" /> Approve</Btn>
            <Btn size="sm" variant="ghost" onClick={() => act.mutate({ verb: "reject" })} data-testid={`post-reject-${post.id}`}><X className="h-3.5 w-3.5 text-err" /> Reject</Btn>
          </>
        )}
        {post.status === "approved" && (
          <Btn size="sm" variant="ghost" onClick={() => onSchedule(post)} data-testid={`post-schedule-${post.id}`}><CalendarClock className="h-3.5 w-3.5 text-primary" /> Schedule</Btn>
        )}
        {(post.status === "approved" || post.status === "scheduled") && (
          <Btn size="sm" variant="ghost" onClick={() => act.mutate({ verb: "publish" })} data-testid={`post-publish-${post.id}`}><Send className="h-3.5 w-3.5 text-primary" /> Publish</Btn>
        )}
        <AlertDialog>
          <AlertDialogTrigger asChild><Btn size="sm" variant="ghost" className="ml-auto" data-testid={`post-del-${post.id}`}><Trash2 className="h-3.5 w-3.5" /></Btn></AlertDialogTrigger>
          <AlertDialogContent className="bg-surface border-border">
            <AlertDialogHeader><AlertDialogTitle>Delete this post?</AlertDialogTitle><AlertDialogDescription>This permanently removes the draft.</AlertDialogDescription></AlertDialogHeader>
            <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={() => del.mutate()} className="bg-err text-white hover:bg-err/90">Delete</AlertDialogAction></AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
}

function PipelineTab() {
  const api = useApi();
  const { brand } = useBrand();
  const [genOpen, setGenOpen] = useState(false);
  const [scheduling, setScheduling] = useState(null);
  const { data: posts = [], isLoading } = useQuery({ queryKey: ["social-posts", brand], queryFn: () => api.get(`/social/posts?brand_id=${brand}`) });
  const cols = [
    { key: "pending", title: "Pending Approval", match: (p) => p.status === "pending_approval" },
    { key: "ready", title: "Approved · Scheduled", match: (p) => p.status === "approved" || p.status === "scheduled" },
    { key: "done", title: "Published", match: (p) => p.status === "published" },
  ];
  return (
    <div data-testid="social-pipeline">
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-muted-foreground">Content for <span className="text-foreground font-mono">{brand}</span> — switch portals from the top bar.</p>
        <Btn onClick={() => setGenOpen(true)} data-testid="social-generate-btn"><Wand2 className="h-4 w-4" /> Generate with AI</Btn>
      </div>
      {isLoading ? <PageLoader /> : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {cols.map((c) => {
            const items = posts.filter(c.match);
            return (
              <div key={c.key} className="neu-card rounded-2xl p-4" data-testid={`pipeline-col-${c.key}`}>
                <div className="flex items-center justify-between mb-3"><SectionTitle>{c.title}</SectionTitle><span className="text-xs font-mono text-muted-foreground">{items.length}</span></div>
                <div className="space-y-3">
                  {items.length === 0 ? <div className="text-xs text-muted-foreground py-6 text-center">Nothing here yet.</div>
                    : items.map((p) => <PostCard key={p.id} post={p} onSchedule={setScheduling} />)}
                </div>
              </div>
            );
          })}
        </div>
      )}
      <GenerateDialog open={genOpen} brand={brand} onClose={() => setGenOpen(false)} />
      {scheduling && <ScheduleDialog post={scheduling} onClose={() => setScheduling(null)} />}
    </div>
  );
}

function GlobalPostTab() {
  const api = useApi();
  const qc = useQueryClient();
  const [form, setForm] = useState({ hook: "", caption: "", hashtags: "" });
  const [platforms, setPlatforms] = useState([]); // empty => all connected per brand
  const [publish, setPublish] = useState(false);
  const { data: preview } = useQuery({ queryKey: ["global-preview"], queryFn: () => api.get("/social/global/preview") });
  const targets = preview?.targets || [];

  const run = useMutation({
    mutationFn: () => api.post("/social/global", {
      hook: form.hook, caption: form.caption,
      hashtags: form.hashtags.split(",").map((h) => h.trim()).filter(Boolean),
      platforms: platforms.length ? platforms : null, publish,
    }),
    onSuccess: (r) => {
      toast.success(`Global post → ${r.targeted} portal(s)${r.skipped ? `, ${r.skipped} skipped` : ""}`);
      setForm({ hook: "", caption: "", hashtags: "" });
      qc.invalidateQueries({ queryKey: ["social-posts"] });
      qc.invalidateQueries({ queryKey: ["social-overview"] });
    },
    onError: (e) => toast.error(e.message),
  });
  const togglePlat = (p) => setPlatforms((cur) => cur.includes(p) ? cur.filter((x) => x !== p) : [...cur, p]);
  const readyCount = targets.filter((t) => t.connected.length).length;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="global-post">
      <div className="neu-card rounded-2xl p-5">
        <div className="flex items-center gap-2 mb-1"><Globe className="h-5 w-5 text-primary" /><SectionTitle>Compose once — publish to all 5 portals</SectionTitle></div>
        <p className="text-xs text-muted-foreground mb-4">Fans out to every brand that has a connected platform. Brands with no connection are skipped automatically.</p>
        <div className="space-y-3">
          <TextInput label="Hook (optional)" value={form.hook} onChange={(e) => setForm({ ...form, hook: e.target.value })} data-testid="global-hook" />
          <TextArea label="Caption" rows={4} value={form.caption} onChange={(e) => setForm({ ...form, caption: e.target.value })} data-testid="global-caption" />
          <TextInput label="Hashtags (comma-separated)" value={form.hashtags} onChange={(e) => setForm({ ...form, hashtags: e.target.value })} data-testid="global-hashtags" />
          <div>
            <div className="text-[11px] uppercase tracking-wider text-muted-foreground mb-1.5">Platforms (leave empty = all connected)</div>
            <div className="flex flex-wrap gap-2">
              {PLATFORMS.map((p) => {
                const Icon = PLAT[p].icon;
                const on = platforms.includes(p);
                return (
                  <button key={p} onClick={() => togglePlat(p)} data-testid={`global-plat-${p}`}
                    className={cn("flex items-center gap-1.5 text-xs rounded-full px-3 py-1.5 border transition-colors",
                      on ? "border-primary/60 bg-primary/10 text-foreground" : "border-border text-muted-foreground hover:text-foreground")}>
                    <Icon className="h-3.5 w-3.5" /> {PLAT[p].label}
                  </button>
                );
              })}
            </div>
          </div>
          <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer" data-testid="global-publish-toggle">
            <input type="checkbox" checked={publish} onChange={(e) => setPublish(e.target.checked)} className="accent-[hsl(var(--primary))]" />
            Publish immediately (otherwise created as drafts for approval)
          </label>
          <Btn onClick={() => run.mutate()} disabled={run.isPending || !form.caption.trim() || readyCount === 0} className="w-full" data-testid="global-post-btn">
            <Globe className="h-4 w-4" /> {run.isPending ? "Broadcasting…" : publish ? "Publish to all portals" : "Create drafts for all portals"}
          </Btn>
          {readyCount === 0 && <p className="text-xs text-warn">No portals have a connected platform yet — connect accounts first in the Connections tab.</p>}
        </div>
      </div>

      <div className="neu-card rounded-2xl p-5" data-testid="global-targets">
        <SectionTitle>Fan-out preview</SectionTitle>
        <p className="text-xs text-muted-foreground mb-3">{readyCount} of {targets.length} portals ready</p>
        <div className="space-y-2">
          {targets.map((t) => (
            <div key={t.code} className="flex items-center justify-between border border-border/60 rounded-lg px-3 py-2">
              <div className="flex items-center gap-2">
                <span className="h-2.5 w-2.5 rounded-full" style={{ background: hsl(t.accent) }} />
                <span className="text-sm font-medium">{t.name}</span>
              </div>
              {t.connected.length ? (
                <div className="flex items-center gap-1.5">
                  {t.connected.map((p) => { const Icon = PLAT[p].icon; return <Icon key={p} className="h-3.5 w-3.5 text-primary" title={PLAT[p].label} />; })}
                </div>
              ) : <span className="text-[10px] uppercase tracking-wider text-muted-foreground">skipped · no connection</span>}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}


export default function Social() {
  const api = useApi();
  const { data: overview } = useQuery({ queryKey: ["social-overview"], queryFn: () => api.get("/social/overview") });
  const c = overview?.connections || { connected: 0, total: 0 };
  const p = overview?.posts || { pending_approval: 0, scheduled: 0 };
  return (
    <div data-testid="social-page">
      <PageHeader kicker="Marketing · Social" title="Social Studio">
        <Share2 className="h-5 w-5 text-primary" />
        <StatusBadge tone="gold">{c.connected}/{c.total} accounts</StatusBadge>
        {p.pending_approval > 0 && <StatusBadge tone="warn" dot="pulse">{p.pending_approval} to review</StatusBadge>}
      </PageHeader>
      <Tabs defaultValue="pipeline">
        <TabsList>
          <TabsTrigger value="pipeline" data-testid="social-tab-pipeline"><Sparkles className="h-4 w-4 mr-1.5" /> Content Pipeline</TabsTrigger>
          <TabsTrigger value="global" data-testid="social-tab-global"><Globe className="h-4 w-4 mr-1.5" /> Global Post</TabsTrigger>
          <TabsTrigger value="connections" data-testid="social-tab-connections"><Share2 className="h-4 w-4 mr-1.5" /> Connections</TabsTrigger>
        </TabsList>
        <TabsContent value="pipeline" className="mt-4"><PipelineTab /></TabsContent>
        <TabsContent value="global" className="mt-4"><GlobalPostTab /></TabsContent>
        <TabsContent value="connections" className="mt-4"><ConnectionsTab /></TabsContent>
      </Tabs>
    </div>
  );
}
